import { ArrowUpRight, Link2Off } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import type {
  StructuralAnalysisCalloutReviewState,
  StructuralAnalysisCalloutView,
} from "@/features/ai/ai.types";

type StructuralAnalysisCalloutCardProps = {
  busy?: boolean;
  callout: StructuralAnalysisCalloutView;
  onOpenChapter?: (callout: StructuralAnalysisCalloutView) => void;
  onSetReviewState: (reviewState: StructuralAnalysisCalloutReviewState) => void;
};

export function StructuralAnalysisCalloutCard({
  busy = false,
  callout,
  onOpenChapter,
  onSetReviewState,
}: StructuralAnalysisCalloutCardProps) {
  const { t } = useTranslation();

  return (
    <article
      className="space-y-4 py-5"
      data-testid="structural-analysis-finding-card"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
            <span>{callout.dimensionLabel}</span>
            <span
              className="rounded-full border border-[#e4d6c6] px-3 py-1 text-[11px] tracking-[0.14em] text-foreground"
              aria-label={`Review state: ${callout.reviewStateLabel}`}
            >
              {callout.reviewStateLabel}
            </span>
          </div>
          <h4 className="text-base font-semibold text-foreground">
            {callout.chapterHref
              ? callout.chapterLabel
              : t("story.ai.structural.report.chapterUnavailableLabel")}
          </h4>
        </div>

        {callout.chapterHref ? (
          <Button
            onClick={() => onOpenChapter?.(callout)}
            size="sm"
            type="button"
            variant="outline"
          >
            {t("story.ai.structural.report.openChapter")}
            <ArrowUpRight className="h-4 w-4" />
          </Button>
        ) : (
          <div className="inline-flex items-center gap-2 rounded-full border border-[#e4d6c6] px-3 py-2 text-xs font-semibold text-muted-foreground">
            <Link2Off className="h-3.5 w-3.5" />
            {t("story.ai.structural.report.chapterUnavailable")}
          </div>
        )}
      </div>

      <p className="text-sm leading-7 text-muted-foreground">{callout.text}</p>

      <div className="flex flex-wrap gap-2">
        <Button
          aria-pressed={callout.reviewState === "acknowledged"}
          disabled={busy}
          onClick={() => onSetReviewState("acknowledged")}
          size="sm"
          type="button"
          variant={callout.reviewState === "acknowledged" ? "default" : "outline"}
        >
          {t("story.ai.structural.report.acknowledge")}
        </Button>
        <Button
          aria-pressed={callout.reviewState === "dismissed"}
          disabled={busy}
          onClick={() => onSetReviewState("dismissed")}
          size="sm"
          type="button"
          variant={callout.reviewState === "dismissed" ? "destructive" : "outline"}
        >
          {t("story.ai.structural.report.dismiss")}
        </Button>
      </div>
    </article>
  );
}
